import React, { useState, useEffect } from 'react';
import { fetchSummaries, fetchStorageDetails } from '../services/api';
import SummaryCard from './SummaryCard';
import ExternalLinks from './ExternalLinks';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';

const SummaryDashboard = () => {
  const [summaries, setSummaries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSummaries = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchSummaries();
      setSummaries(data);
    } catch (err) {
      setError('Αποτυχία φόρτωσης δεδομένων');
      console.error('Error loading summaries:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummaries();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Φόρτωση...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="ml-2 text-red-700">{error}</span>
          <button
            onClick={loadSummaries}
            className="ml-4 flex items-center rounded bg-red-100 px-3 py-1 text-sm text-red-700 hover:bg-red-200"
          >
            <RefreshCw className="mr-1 h-4 w-4" />
            Επανάληψη
          </button>
        </div>
      </div>
    );
  }

  if (!summaries) return null;

  const sections = [
    {
      title: 'Γενικά',
      items: [
        { key: 'total', label: 'Σύνολο αποθήκης', variant: 'info' },
        { key: 'danger', label: 'Για έλεγχο', variant: 'danger' },
        { key: 'expected', label: 'Ελ. Αν Χαρμοβίτου', variant: 'default' },
        { key: 'expectedLogistics', label: 'Ελ. Αν Logistics', variant: 'default' },
      ],
    },
    {
      title: 'Τοξικά',
      items: [
        { key: 'toxic', label: 'Τοξικά', variant: 'warning' },
        { key: 'toxicByPrice', label: 'Τοξικά Λόγο Τιμής', variant: 'warning' },
        { key: 'toxicByMarketability', label: 'Τοξικά Λόγο Εμπορικότητας', variant: 'warning' },
        { key: 'toxicBoth', label: 'Τοξικά Και τα δύο', variant: 'warning' },
        { key: 'toxic1Sale', label: 'Τοξικά με <1 πώληση 60 ημ', variant: 'warning' },
        { key: 'toxic365', label: 'Τοξικά >365 ημέρες', variant: 'danger' },
        { key: 'toxic730', label: 'Τοξικά >730 ημέρες', variant: 'danger' },
        { key: 'toxic3', label: 'Υπερτοξικά', variant: 'danger' },
      ],
    },
    {
      title: 'Προσωρινές Αποθήκες',
      items: [
        { key: 'temporary', label: 'Προσωρινή αποθήκη', variant: 'default' },
        { key: 'lost', label: 'Απολεσθέντα', variant: 'danger' },
        { key: 'destruction', label: 'Καταστροφή', variant: 'danger' },
        { key: 'nonFunctional', label: 'Μη λειτουργικά', variant: 'danger' },
        { key: 'otp', label: 'ΟΤΠ', variant: 'default' },
        { key: 'ebay', label: 'EBAY', variant: 'default' },
        { key: 'courier', label: 'Courier', variant: 'default' },
        { key: 'resale', label: 'Μεταπώληση', variant: 'default' },
        { key: 'fromSupplier', label: 'Αναμονή από προμ.', variant: 'default' },
      ],
    },
    {
      title: 'Άλλα',
      items: [
        { key: 'failedPurchases', label: 'Αποτυχημένες αγορές', variant: 'warning' },
        { key: 'wrongShipments', label: 'Λάθος αποστολές', variant: 'warning' },
        { key: 'compensated', label: 'Αποζημιωθέντα', variant: 'default' },
        { key: 'workExclude', label: 'EΞαιρούμενα', variant: 'default' },
        { key: 'forGreek', label: 'Για ελληνικά', variant: 'default' },
        { key: 'checked', label: 'Ελεγμένα', variant: 'success' },
        { key: 'fromInventory', label: 'Από απογραφή', variant: 'default' },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">Σύνοψη Αποθήκης</h2>
        <button
          onClick={loadSummaries}
          className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Ανανέωση
        </button>
      </div>

      {sections.map((section) => (
        <div key={section.title}>
          <h3 className="mb-3 text-lg font-semibold text-gray-700">{section.title}</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {section.items.map((item) => {
              const data = summaries[item.key];
              if (!data) return null;
              return (
                <SummaryCard
                  key={item.key}
                  label={item.label}
                  total={data.total}
                  count={data.count}
                  variant={item.variant}
                />
              );
            })}
          </div>
        </div>
      ))}

      {/* External Links */}
      <ExternalLinks />
    </div>
  );
};

export default SummaryDashboard;
